/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { useState, useCallback } from 'react';

/**
 * Hook that provides a smart setError function that only creates/sets error objects
 * if the error message would be different from the current error.
 * This avoids unnecessary object creation and re-renders when the same error persists.
 *
 * @returns A tuple of [error, setError] where setError takes a factory function
 */
export function useErrorState(): [Error | null, (errorFactory: (() => Error) | null) => void] {
  const [error, setErrorState] = useState<Error | null>(null);

  const setError = useCallback((errorFactory: (() => Error) | null) => {
    if (errorFactory === null) {
      setErrorState(null);
      return;
    }
    setErrorState((currentError) => {
      const newError = errorFactory();
      if (currentError?.message === newError.message) {
        return currentError; // Return existing error to avoid unnecessary re-render
      }
      return newError;
    });
  }, []);

  return [error, setError];
}
